import { PromotionPlanTypeId } from '@/enums/PromotionPlanType.enum';
import { ApiHosts, handleResponseOld, makeGet } from '@/redux/api/helpers';
import { ConfigSlice } from '@/redux/modules/config';
import { FetchItemsSummariesResponse, transformItemSummary } from '@/types/item/ItemSummary';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';

export type CatalogFeaturedLot = {
    lotId: number;
    planTypeId: PromotionPlanTypeId;
};

/**
 * @category ItemSummary API
 * @see getCatalogFeaturedLots
 */
type GetCatalogFeaturedLotsResponse = {
    data: {
        error: boolean;
        payload: CatalogFeaturedLot[];
    };
    message: string;
    success: boolean;
};

/**
 * @category ItemSummary API
 * @see getCatalogFeaturedLots
 */
type GetCatalogFeaturedLotsParams = {
    authToken: string;
    catalogId: number;
    deployment: ConfigSlice['deployment'];
};

/**
 * Fetches the promoted (featured) lots for a catalog, with the promotion plan type each was
 * purchased under, via Get to ItemSummary service
 * @function getCatalogFeaturedLots
 * @category ItemSummary API
 * @param {GetCatalogFeaturedLotsParams}
 */
export const getCatalogFeaturedLots = ({ authToken, catalogId, deployment }: GetCatalogFeaturedLotsParams) =>
    new Promise<CatalogFeaturedLot[]>((resolve, reject) => {
        const request = makeGet({
            apiPath: `spa/small/catalog/${catalogId}/featured-lots`,
            authToken,
            deployment,
            path: ApiHosts.ItemApi,
        });
        request.end((err, response) =>
            handleResponseOld({
                err,
                reject,
                resolve: (data: GetCatalogFeaturedLotsResponse) => resolve(data?.data?.payload ?? []),
                response,
            })
        );
    });

/**
 * @category ItemSummary API
 * @see fetchItemsByIds
 */
type FetchItemSummariesByIdsParams = {
    authToken: string;
    deployment: string;
    identifier: string;
    itemIds: number[];
    // Used on the console to ensure the bidder's bidding status is correct on page load
    liveStateFetch?: boolean;
    similarItems?: boolean;
};

/**
 * Fetches item info by given item ids via Post to ItemSummary service
 * @function fetchItemSummariesByIds
 * @category ItemSummary API
 * @param {FetchItemSummariesByIdsParams}
 */
export const fetchItemSummariesByIds = ({
    authToken,
    deployment,
    identifier,
    itemIds,
    liveStateFetch,
    similarItems,
}: FetchItemSummariesByIdsParams) =>
    new Promise<FetchItemsSummariesResponse>((resolve, reject) => {
        const request = makeGet({
            apiPath: 'items',
            authToken,
            deployment,
            path: ApiHosts.ItemModel,
        });
        request.query({
            identifier,
            liveStateFetch,
            lotIds: itemIds.join(','),
            similarItems,
        });
        request.end((err, response) =>
            handleResponseOld({
                err,
                reject,
                resolve,
                response,
                transform: transformItemSummary,
            })
        );
    });

export type FetchItemSummariesByCatalogIdQuery = {
    buyNow?: boolean;
    index?: number;
    keyword?: string;
    page?: number;
    pageSize?: number | 'all';
    preview?: boolean;
    sort?: SearchSortAndDirection;
};

type FetchItemSummariesByCatalogIdParams = {
    authToken: string;
    catalogId: number;
    deployment: string;
    query: FetchItemSummariesByCatalogIdQuery;
};

/**
 * Fetches item info by given item ids via Post to ItemSummary service
 * @function fetchItemSummariesByCatalogId
 * @category ItemSummary API
 * @param {FetchItemSummariesByIdsParams}
 */
export const fetchItemSummariesByCatalogId = ({
    authToken,
    catalogId,
    deployment,
    query,
}: FetchItemSummariesByCatalogIdParams) =>
    new Promise<FetchItemsSummariesResponse>((resolve: Function, reject: Function) => {
        const request = makeGet({
            apiPath: `catalog/${catalogId}`,
            authToken,
            deployment,
            path: ApiHosts.ItemModel,
        });
        request.query({
            ...query,
        });
        request.end((err, response) =>
            handleResponseOld({
                err,
                reject,
                resolve,
                response,
                transform: transformItemSummary,
            })
        );
    });
